import { useState, useEffect } from 'react';

export interface ColumnSetting {
  key: string;
  title: string;         // original title
  displayTitle: string;  // possibly renamed
  visible: boolean;
  order: number;
  width?: number;
}

export function useColumnSettings(
  tableKey: string,
  initialColumns: { key: string; title: string; width?: number }[],
  scope?: { userId?: string; tenantId?: string }
) {
  const scopeKey = scope?.tenantId && scope?.userId
    ? `${scope.tenantId}_${scope.userId}`
    : 'default';
  const storageKey = `erp_col_${scopeKey}_${tableKey}`;

  const [settings, setSettings] = useState<ColumnSetting[]>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed: ColumnSetting[] = JSON.parse(stored);
        // Merge with initialColumns to handle added/removed columns
        return initialColumns.map((col, i) => {
          const found = parsed.find((s) => s.key === col.key);
          return found ?? {
            key: col.key,
            title: col.title,
            displayTitle: col.title,
            visible: true,
            order: i,
            width: col.width,
          };
        });
      }
    } catch { /* ignore */ }
    return initialColumns.map((col, i) => ({
      key: col.key,
      title: col.title,
      displayTitle: col.title,
      visible: true,
      order: i,
      width: col.width,
    }));
  });

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(settings));
  }, [settings, storageKey]);

  const setVisible = (key: string, visible: boolean) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, visible } : s)));
  };

  const setDisplayTitle = (key: string, displayTitle: string) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, displayTitle } : s)));
  };

  const reorder = (fromIndex: number, toIndex: number) => {
    setSettings((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next.map((s, i) => ({ ...s, order: i }));
    });
  };

  const setWidth = (key: string, width?: number) => {
    setSettings((prev) => prev.map((s) => (s.key === key ? { ...s, width } : s)));
  };

  const reset = () => {
    localStorage.removeItem(storageKey);
    setSettings(
      initialColumns.map((col, i) => ({
        key: col.key,
        title: col.title,
        displayTitle: col.title,
        visible: true,
        order: i,
        width: col.width,
      }))
    );
  };

  return { settings, setVisible, setDisplayTitle, setWidth, reorder, reset };
}
